import { Constants, Defaults } from '../common/constants.js';
import type { PopConfiguration } from '../../types.js';

export class ProductComponentLibraryOrchestration {
  constructor() {
    // Intentionally Empty
  }

  /**
   * When using `URL` the base arg will be trimmed
   *  until the first slash, so this ensures an
   *  ending slash
   */
  checkSlash(str: string): string {
    return str.endsWith('/') ? str : `${str}/`;
  }

  /**
   * Checks if a local version of POP should be used
   */
  isLocal(): boolean {
    const popVersion =
      window[Constants.POP_CONFIGURATION].productComponentLibraryVersion;
    if (popVersion && popVersion === 'local') {
      return true;
    }
    return false;
  }

  /**
   * Checks if orchestration should skip registration setup
   */
  skipRegister(): boolean {
    const skip = window[Constants.POP_CONFIGURATION].popSkip;
    if (skip === 'true' || skip === true) {
      return true;
    }
    return false;
  }

  /**
   * Generate a assets URL root
   * @returns URL to a directory that contains Pop/components assets
   */
  getPopRootBase(): string {
    const windowProp = window[Constants.POP_CONFIGURATION];
    const assetsDomain = windowProp.assetsDomain || Defaults.ASSETS_DOMAIN;
    const popVersion =
      windowProp.productComponentLibraryVersion ||
      Defaults.PRODUCT_COMPONENT_LIBRARY_VERSION;
    let popRootBase = new URL(
      Constants.POP_URL_ROOT_PATH,
      this.checkSlash(assetsDomain),
    ).href;
    if (popVersion !== 'latest') {
      // different url when not `latest`
      popRootBase = new URL(
        Constants.POP_URL_ROOT_VERSIONED,
        this.checkSlash(assetsDomain),
      ).href;
    }
    popRootBase = new URL(popVersion, this.checkSlash(popRootBase)).href;
    // compensates when `popVersion` includes `versioned/`
    return popRootBase.replace('versioned/versioned', 'versioned');
  }

  /**
   * Generates the full URL to the register script
   */
  getPopComponentsRegister(): string {
    const popRootBase = this.getPopRootBase();
    return new URL(
      'packages/components/register.js',
      this.checkSlash(popRootBase),
    ).href;
  }

  /**
   * Creates config from `window` and registers POP components
   */
  public async registerComponents(popComponentsRegister): Promise<void> {
    const windowProp: PopConfiguration = window[Constants.POP_CONFIGURATION];
    const configMap = new Map<string, string>();
    configMap.set(Constants.DADA_ENDPOINT, windowProp.dadaEndpoint);
    configMap.set(Constants.DADA_APIKEY, windowProp.dadaApiKey);
    configMap.set(Constants.ASSETS_DOMAIN, windowProp.assetsDomain);
    configMap.set(
      Constants.PRODUCT_COMPONENT_LIBRARY_VERSION,
      windowProp.productComponentLibraryVersion,
    );
    configMap.set(Constants.IVS_ENDPOINT, windowProp.ivsEndpoint);
    configMap.set(Constants.IVS_APIKEY, windowProp.ivsApiKey);
    configMap.set(Constants.COUNTRY, windowProp.country);
    configMap.set(Constants.LANGUAGE, windowProp.language);

    await popComponentsRegister.registerComponents([], true, configMap);
  }

  /**
   * Loads POP components onto a page
   * if `.productComponentLibraryVersion` is `local`, POP
   *  will not be registered.
   */
  public async loadProductComponentLibrary(): Promise<void> {
    if (this.isLocal()) {
      console.log(Constants.LOG_POP_LOCAL_VERSION);
      Promise.resolve();
      return;
    }
    if (this.skipRegister()) {
      console.log(Constants.LOG_POP_SKIP_REGISTER);
      Promise.resolve();
      return;
    }
    const popComponentsRegister = this.getPopComponentsRegister();
    await import(/* @vite-ignore */ popComponentsRegister)
      .then(async (module) => {
        await this.registerComponents(module);
      })
      .catch((error) =>
        console.error(Constants.LOG_IMPORT_BEACON_ERROR, error),
      );
  }
}
